import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { ordersService, CreateOrderData } from '../services/ordersService';
import { orderService } from '../services/orderService';

// Types
export interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  quantity: number;
  maxQuantity: number; // Stock disponible
  features: string[];
  partner: string;
  inStock: boolean;
  category?: string;
}

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  company?: string;
}

export interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal' | 'stripe';
  last4?: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  notes?: string;
}

export interface Order {
  id: string;
  userId: string;
  items: CartItem[];
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  status: 'pending' | 'processing' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  shippingAddress: ShippingAddress;
  paymentMethod: PaymentMethod;
  paymentStatus: 'pending' | 'processing' | 'paid' | 'failed' | 'refunded' | 'partially_refunded';
  createdAt: Date;
  updatedAt: Date;
  trackingNumber?: string;
  notes?: string;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  // Controls the multi-step checkout modal visibility
  isCheckoutOpen: boolean;
  isLoading: boolean;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  itemCount: number;
  shippingAddress: ShippingAddress | null;
  paymentMethod: PaymentMethod | null;
  currentOrder: Order | null;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'quantity'> & { quantity?: number } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'TOGGLE_CART' }
  | { type: 'SET_CART_OPEN'; payload: boolean }
  | { type: 'SET_SHIPPING_ADDRESS'; payload: ShippingAddress }
  | { type: 'SET_PAYMENT_METHOD'; payload: PaymentMethod }
  | { type: 'SET_CURRENT_ORDER'; payload: Order | null }
  | { type: 'LOAD_CART'; payload: CartItem[] }
  | { type: 'SET_CHECKOUT_OPEN'; payload: boolean };

interface CartContextType extends CartState {
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  setCartOpen: (open: boolean) => void;
  setShippingAddress: (address: ShippingAddress) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  proceedToCheckout: () => Promise<string>; // Returns checkout URL
  createOrder: (paymentIntentId?: string) => Promise<Order>;
  validateInventory: () => Promise<boolean>;
  openCheckout: () => void;
  closeCheckout: () => void;
  getItemById: (id: string) => CartItem | undefined;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  hasItems: () => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      const quantity = action.payload.quantity || 1;
      
      if (existingItem) {
        const newQuantity = Math.min(existingItem.quantity + quantity, action.payload.maxQuantity);
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: newQuantity }
            : item
        );
        return { ...state, items: updatedItems };
      }
      
      const newItem: CartItem = { ...action.payload, quantity };
      return { ...state, items: [...state.items, newItem] };
    }
    
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case 'UPDATE_QUANTITY': {
      const updatedItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: Math.min(Math.max(action.payload.quantity, 0), item.maxQuantity) }
          : item
      ).filter(item => item.quantity > 0);
      
      return { ...state, items: updatedItems };
    }
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        shippingAddress: null,
        paymentMethod: null,
        currentOrder: null
      };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };
    
    case 'SET_CART_OPEN':
      return { ...state, isOpen: action.payload };
    
    case 'SET_CHECKOUT_OPEN':
      return { ...state, isCheckoutOpen: action.payload };
    
    case 'SET_SHIPPING_ADDRESS':
      return { ...state, shippingAddress: action.payload };
    
    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethod: action.payload };
    
    case 'SET_CURRENT_ORDER':
      return { ...state, currentOrder: action.payload };
    
    case 'LOAD_CART':
      return { ...state, items: action.payload };
    
    default:
      return state;
  }
};

// Calculate totals
const calculateTotals = (items: CartItem[], country: string = 'MX') => {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Tax rates by country
  const taxRates = {
    'MX': 0.16, // IVA México
    'EC': 0.12, // IVA Ecuador  
    'PE': 0.18, // IGV Perú
    'INTL': 0.10 // International
  };
  
  const tax = subtotal * (taxRates[country as keyof typeof taxRates] || taxRates.INTL);
  
  // Shipping calculation (free over $500)
  const shipping = subtotal > 500 ? 0 : 50;
  
  const total = subtotal + tax + shipping;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  
  return { subtotal, tax, shipping, total, itemCount };
};

// Initial state
const initialState: CartState = {
  items: [],
  isOpen: false,
  isCheckoutOpen: false,
  isLoading: false,
  subtotal: 0,
  tax: 0,
  shipping: 0,
  total: 0,
  itemCount: 0,
  shippingAddress: null,
  paymentMethod: null,
  currentOrder: null,
};

// Hook
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Provider
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user, isAuthenticated } = useAuth();
  
  // Calculate totals whenever items change
  const totals = calculateTotals(state.items);
  const enhancedState = { ...state, ...totals };
  
  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('chefmax_cart');
    if (savedCart) {
      try {
        const items = JSON.parse(savedCart);
        dispatch({ type: 'LOAD_CART', payload: items });
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('chefmax_cart', JSON.stringify(state.items));
  }, [state.items]);
  
  // Actions
  const addItem = (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    if (!item.inStock) {
      toast.error('Producto agotado');
      return;
    }
    
    const quantity = item.quantity || 1;
    if (quantity > item.maxQuantity) {
      toast.error(`Solo hay ${item.maxQuantity} unidades disponibles`);
      return;
    }
    
    // Compute next items synchronously to persist immediately
    const existing = state.items.find(i => i.id === item.id);
    let nextItems: CartItem[];
    if (existing) {
      const qty = Math.min((item.quantity || 1) + existing.quantity, existing.maxQuantity);
      nextItems = state.items.map(i => i.id === item.id ? { ...i, quantity: qty } : i);
    } else {
      const newItem: CartItem = { ...item, quantity: item.quantity || 1 } as CartItem;
      nextItems = [...state.items, newItem];
    }
    try { localStorage.setItem('chefmax_cart', JSON.stringify(nextItems)); } catch {}
    dispatch({ type: 'ADD_ITEM', payload: item });
    toast.success(`${item.name} agregado al carrito`);
  };
  
  const removeItem = (id: string) => {
    const nextItems = state.items.filter(i => i.id !== id);
    try { localStorage.setItem('chefmax_cart', JSON.stringify(nextItems)); } catch {}
    dispatch({ type: 'REMOVE_ITEM', payload: id });
    toast.success('Producto eliminado del carrito');
  };
  
  const updateQuantity = (id: string, quantity: number) => {
    const item = state.items.find(i => i.id === id);
    if (item && quantity > item.maxQuantity) {
      toast.error(`Solo hay ${item.maxQuantity} unidades disponibles`);
      return;
    }
    const nextItems = state.items
      .map(i => i.id === id ? { ...i, quantity: Math.min(Math.max(quantity, 1), i.maxQuantity) } : i)
      .filter(i => i.quantity > 0);
    try { localStorage.setItem('chefmax_cart', JSON.stringify(nextItems)); } catch {}
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };
  
  const clearCart = () => {
    try { localStorage.setItem('chefmax_cart', JSON.stringify([])); } catch {}
    dispatch({ type: 'CLEAR_CART' });
    toast.success('Carrito vaciado');
  };
  
  const toggleCart = () => {
    dispatch({ type: 'TOGGLE_CART' });
  };
  
  const setCartOpen = (open: boolean) => {
    dispatch({ type: 'SET_CART_OPEN', payload: open });
  };
  
  // Global checkout modal controls
  const openCheckout = () => {
    console.log('[Cart] openCheckout');
    dispatch({ type: 'SET_CHECKOUT_OPEN', payload: true });
  };
  const closeCheckout = () => {
    console.log('[Cart] closeCheckout');
    dispatch({ type: 'SET_CHECKOUT_OPEN', payload: false });
  };
  
  const setShippingAddress = (address: ShippingAddress) => {
    dispatch({ type: 'SET_SHIPPING_ADDRESS', payload: address });
  };
  
  const setPaymentMethod = (method: PaymentMethod) => {
    dispatch({ type: 'SET_PAYMENT_METHOD', payload: method });
  };
  
  const validateInventory = async (): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Call real backend validation
      const validation = await orderService.validateCartItems(state.items);
      
      if (!validation.valid) {
        validation.errors.forEach(error => toast.error(error));
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Inventory validation error:', error);
      toast.error('Error validando inventario');
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };
  
  const proceedToCheckout = async (): Promise<string> => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para continuar');
      throw new Error('User not authenticated');
    }
    
    if (state.items.length === 0) {
      toast.error('El carrito está vacío');
      throw new Error('Cart is empty');
    }
    
    const isValid = await validateInventory();
    if (!isValid) {
      throw new Error('Inventory validation failed');
    }
    
    // Modal flow: return a marker (no navigation)
    return 'modal';
  };
  
  const createOrder = async (paymentIntentId?: string): Promise<Order> => {
    if (!user || !state.shippingAddress || !state.paymentMethod) {
      throw new Error('Missing required order information');
    }
    
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Prepare order data for backend
      const orderData: CreateOrderData = {
        items: orderService.convertCartToOrderItems(state.items),
        ...orderService.calculateOrderTotals(
          enhancedState.subtotal,
          0.16, // 16% tax rate (Mexico)
          enhancedState.shipping,
          0 // no discount for now
        ),
        currency: 'USD',
        shippingAddress: state.shippingAddress,
        billingAddress: state.shippingAddress, // Use same address for now
        customerInfo: {
          email: user.email,
          phone: state.shippingAddress.phone,
          firstName: state.shippingAddress.firstName,
          lastName: state.shippingAddress.lastName
        },
        paymentMethod: state.paymentMethod.type,
        notes: state.paymentMethod.notes
      };

      // Create order in backend
      const backendOrder = await ordersService.createOrder(orderData);
      
      // Convert backend order to frontend format
      const order: Order = {
        id: backendOrder.id,
        userId: backendOrder.user_id,
        items: state.items, // Keep frontend format for now
        subtotal: backendOrder.subtotal,
        tax: backendOrder.tax_amount,
        shipping: backendOrder.shipping_amount,
        total: backendOrder.total_amount,
        status: backendOrder.status,
        shippingAddress: backendOrder.shipping_address,
        paymentMethod: state.paymentMethod,
        paymentStatus: (backendOrder.payment_status as 'pending' | 'processing' | 'paid' | 'failed' | 'refunded' | 'partially_refunded') || 'pending',
        createdAt: new Date(backendOrder.created_at),
        updatedAt: new Date(backendOrder.updated_at),
        trackingNumber: backendOrder.tracking_number || `TN${Date.now()}`,
      };
      
      dispatch({ type: 'SET_CURRENT_ORDER', payload: order });
      
      // Clear cart after successful order
      dispatch({ type: 'CLEAR_CART' });
      toast.success('¡Pedido creado exitosamente!');
      
      return order;
    } catch (error) {
      console.error('Order creation error:', error);
      toast.error('Error creando el pedido');
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };
  
  const getItemById = (id: string): CartItem | undefined => {
    return state.items.find(item => item.id === id);
  };
  
  const getTotalItems = (): number => {
    return enhancedState.itemCount;
  };
  
  const getTotalPrice = (): number => {
    return enhancedState.total;
  };
  
  const hasItems = (): boolean => {
    return state.items.length > 0;
  };
  
  const value: CartContextType = {
    ...enhancedState,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    setCartOpen,
    openCheckout,
    closeCheckout,
    setShippingAddress,
    setPaymentMethod,
    proceedToCheckout,
    createOrder,
    validateInventory,
    getItemById,
    getTotalItems,
    getTotalPrice,
    hasItems,
  };

  // Expose helpers for quick manual testing
  useEffect(() => {
    (window as any).openCheckout = openCheckout;
    (window as any).closeCheckout = closeCheckout;
  }, [openCheckout, closeCheckout]);
  
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
